// Resolves the TIDA threat profile for an assessment using a fixed cascade
// so consumers (insights pages, impact calibration, regulatory context, AI
// generation) all get the same answer.
//
// Resolution order:
//   1. Explicitly saved profile from a prior Threat Context save
//      (responses.threatContext.profileKey or notes.threatContext.profileKey)
//   2. Industry/sub-industry-driven recommendation via recommendProfile()
//      — this is what the user expects to "just work" once they pick the
//      client's industry on the Client Overview.
//   3. TP-DEFAULT — the generic baseline used as last resort.
//
// resolveTunedThreatProfile() additionally applies the Client Intelligence
// overlay engine, returning weight adjustments + named-actor relevance.

import threatProfiles from '../data/threatProfiles.json'
import { recommendProfile } from './profileRecommendation'
import { applyClientIntelligenceOverlays } from './threatProfileOverlays'

export function resolveThreatProfile({ client, responses }) {
  const r = responses || {}

  // 1. Saved profile
  const savedKey = r.threatContext?.profileKey || r.notes?.threatContext?.profileKey
  if (savedKey) {
    const profile = threatProfiles.profiles.find(p => p.verticalKey === savedKey)
    if (profile) {
      return { profile, source: 'saved', verticalKey: profile.verticalKey }
    }
  }

  // 2. Industry-driven recommendation
  if (client) {
    try {
      const rec = recommendProfile({ client, businessScoping: r.businessScoping })
      if (rec?.profileId) {
        const profile = threatProfiles.profiles.find(p => p.verticalKey === rec.profileId)
        if (profile) {
          return { profile, source: 'industry', verticalKey: profile.verticalKey, recommendation: rec }
        }
      }
    } catch (err) {
      // recommendProfile shouldn't throw, but guard so resolver never fails.
      console.warn('[resolveThreatProfile] Recommendation failed:', err?.message)
    }
  }

  // 3. Last resort
  const defaultProfile = threatProfiles.profiles.find(p => p.verticalKey === 'TP-DEFAULT') || null
  return { profile: defaultProfile, source: 'default', verticalKey: 'TP-DEFAULT' }
}

// Returns the resolved baseline profile plus Client Intelligence overlay
// outputs (weight adjustments, named actors, evidence). Use this when you
// need the personalized view (insights pages, advisory report).
export function resolveTunedThreatProfile({ client, responses, enrichmentRow, technologyStack = [] }) {
  const base = resolveThreatProfile({ client, responses })
  const overlay = applyClientIntelligenceOverlays({
    profile: base.profile,
    enrichmentRow,
    businessScoping: responses?.businessScoping,
    technologyStack
  })
  return { ...base, overlay }
}
